import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Building2, MapPin, Phone, Mail, CheckCircle2 } from "lucide-react";

export default function PublicLanding() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/landing/by-slug/${slug}`);
        setPage(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/landing/by-slug/${slug}/lead`, form);
      setSent(true);
      toast.success("Thanks! The agent will be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (notFound) return <div className="min-h-screen bg-ink text-white flex items-center justify-center">Page not found.</div>;
  if (!page) return <div className="min-h-screen bg-ink flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-champagne animate-spin" /></div>;

  const brand = page.agent?.brand_color || "#D4AF37";
  const photos = page.photos || [];
  const hero = photos[0] ? `${API}/files/${photos[0]}` : null;

  return (
    <div className="min-h-screen bg-ink text-white font-sans" data-testid="public-landing">
      <section className="relative min-h-[70vh] flex items-end">
        {hero ? <img src={hero} alt={page.title} className="absolute inset-0 w-full h-full object-cover" />
              : <div className="absolute inset-0 bg-ink-surface" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/60 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-6 pb-12 w-full">
          <span className="inline-block px-3 py-1 rounded text-xs font-bold uppercase tracking-wide" style={{ background: brand, color: "#0B0E14" }}>For Sale</span>
          <h1 className="font-display text-4xl sm:text-6xl mt-4 leading-tight">{page.title || "Featured Listing"}</h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-zinc-300">
            {page.price && <span className="text-2xl font-semibold" style={{ color: brand }}>{page.price}</span>}
            {page.address && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {page.address}</span>}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          {page.headline && <h2 className="font-display text-3xl mb-4">{page.headline}</h2>}
          <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap text-lg">{page.description}</p>
          {photos.length > 1 && (
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {photos.slice(1).map((p, i) => (
                <img key={i} src={`${API}/files/${p}`} alt={`Photo ${i + 2}`} className="rounded-lg w-full h-56 object-cover border border-hairline" />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-ink-surface border border-hairline rounded-xl p-6 sticky top-6">
            {sent ? (
              <div className="text-center py-6" data-testid="lead-success">
                <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: brand }} />
                <p className="mt-3 font-semibold">Request sent!</p>
                <p className="text-zinc-400 text-sm mt-1">The agent will reach out shortly.</p>
              </div>
            ) : (
              <>
                <h3 className="font-semibold text-lg">Request a showing</h3>
                <form onSubmit={submit} className="space-y-3 mt-4">
                  <Input required placeholder="Your name" data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 text-white" />
                  <Input required type="email" placeholder="Email" data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/30 border-white/10 text-white" />
                  <Input placeholder="Phone (optional)" data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/30 border-white/10 text-white" />
                  <Textarea placeholder="Message" data-testid="lead-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-black/30 border-white/10 text-white" rows={3} />
                  <Button type="submit" disabled={sending} data-testid="lead-submit" className="w-full font-semibold" style={{ background: brand, color: "#0B0E14" }}>
                    {sending ? "Sending…" : "Request info"}
                  </Button>
                </form>
              </>
            )}
            {page.agent?.name && (
              <div className="mt-6 pt-5 border-t border-hairline flex items-center gap-3">
                {page.agent.headshot_path && <img src={`${API}/files/${page.agent.headshot_path}`} alt={page.agent.name} className="h-12 w-12 rounded-full object-cover" />}
                <div className="text-sm">
                  <p className="font-semibold">{page.agent.name}</p>
                  {page.agent.phone && <p className="text-zinc-400 flex items-center gap-1"><Phone className="h-3 w-3" /> {page.agent.phone}</p>}
                  {page.agent.email && <p className="text-zinc-400 flex items-center gap-1"><Mail className="h-3 w-3" /> {page.agent.email}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-hairline py-6 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
        <Building2 className="h-4 w-4 text-champagne" /> Powered by PenhausAI
      </footer>
    </div>
  );
}
